import { useEffect, useRef, useState, type ReactNode } from "react";
import { ShoppingBag, Menu, X, ChevronDown, Search, Heart } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { navGroups, type NavGroup } from "@/lib/navigation";
import { COLOR_CATEGORIES } from "@/lib/colors";
import { SIZE_BUCKETS } from "@/lib/sizes";
import { useT, type DictKey } from "@/lib/i18n";
import { useWishlist } from "@/hooks/useWishlist";

import { LocaleLink } from "./LocaleLink";
import { LangSwitcher } from "./LangSwitcher";
import { SearchPanel } from "@/components/SearchPanel";

type CategoryKey = "tableware" | "glass" | "interior";
type DropdownKey = CategoryKey | "murano" | "colors" | "sizes" | null;

// The Murano nav dropdown shares the same group from `navGroups` that powers
// the main "Shop" mega-menu, but rendered as its own focused panel.
const MURANO_GROUP = navGroups.find((g) => g.labelKey === "nav.murano");

// Top-level category dropdowns (besides Murano + Farver + Størrelse). Each one
// mirrors a group from `navGroups`. Sub-collections are rendered with hand-
// drawn silhouettes — see SHAPE_BY_HANDLE — so the dropdowns read as small
// curated icon-sets rather than text columns.
const CATEGORY_NAVS: Array<{
  key: CategoryKey;
  groupKey: DictKey;
  labelDa: string;
  labelEn: string;
  italian: string;
  allHandle: string;
  blurbDa: string;
  blurbEn: string;
}> = [
  {
    key: "tableware",
    groupKey: "nav.tableware",
    labelDa: "Borddækning",
    labelEn: "Tableware",
    italian: "A tavola",
    allHandle: "borddaekning",
    blurbDa: "Tallerkener, fade og bestik med patina.",
    blurbEn: "Plates, platters and cutlery with patina.",
  },
  {
    key: "glass",
    groupKey: "nav.glass",
    labelDa: "Glas",
    labelEn: "Glass",
    italian: "Vetro",
    allHandle: "glas",
    blurbDa: "Vinglas, karaffer og glaskunst.",
    blurbEn: "Wine glasses, decanters and glass art.",
  },
  {
    key: "interior",
    groupKey: "nav.interior",
    labelDa: "Interiør",
    labelEn: "Interior",
    italian: "Interno",
    allHandle: "interior",
    blurbDa: "Vaser, spejle, lysestager og små detaljer.",
    blurbEn: "Vases, mirrors, candleholders and small details.",
  },
];

function findGroup(key: DictKey): NavGroup | undefined {
  return navGroups.find((g) => g.labelKey === key);
}

// Four Murano-inspired lamp silhouettes used in the "Farver" dropdown (rotated
// through colors so each color maps to a different lamp shape).
const LAMP_SHAPES: ReactNode[] = [
  <>
    <path d="M5 22 C5 11, 14 4, 20 4 C26 4, 35 11, 35 22 L33 24 L7 24 Z" />
    <rect x="16" y="24" width="8" height="16" rx="0.8" />
    <rect x="11" y="40" width="18" height="3" rx="1.2" />
  </>,
  <>
    <path d="M9 20 C9 9, 14 4, 20 4 C26 4, 31 9, 31 20 C31 26, 27 28, 20 28 C13 28, 9 26, 9 20 Z" />
    <rect x="17" y="28" width="6" height="10" />
    <path d="M10 38 L30 38 L27 43 L13 43 Z" />
  </>,
  <>
    <rect x="19.4" y="2" width="1.2" height="10" />
    <path d="M6 24 C6 16, 12 12, 20 12 C28 12, 34 16, 34 24 C34 28, 30 30, 20 30 C10 30, 6 28, 6 24 Z" />
    <ellipse cx="20" cy="30" rx="9" ry="2.2" />
  </>,
  <>
    <path d="M14 6 L26 6 L26 10 C30 14, 32 20, 28 26 C26 28, 22 29, 20 29 C18 29, 14 28, 12 26 C8 20, 10 14, 14 10 Z" />
    <rect x="17" y="29" width="6" height="11" />
    <path d="M11 40 L29 40 L31 44 L9 44 Z" />
  </>,
];

// Per-collection silhouettes used in the Borddækning, Glas, Interiør and
// Murano dropdowns. All shapes designed on a 40x48 viewBox with the base
// resting around y=42–44 so they line up consistently. Keep paths simple —
// these read at ~32px and need to be recognisable, not photo-real.
const TableShapes = {
  // Side-view flat dish — the classic everyday plate
  plate: (
    <>
      <path d="M3 24 L37 24 L33 30 L7 30 Z" />
      <ellipse cx="20" cy="24" rx="17" ry="2" />
    </>
  ),
  // Wider, shallower oval — a serving platter
  platter: (
    <>
      <ellipse cx="20" cy="26" rx="17" ry="3" />
      <ellipse cx="20" cy="24" rx="13" ry="1.6" />
    </>
  ),
  // Side bowl with curved walls
  bowl: (
    <>
      <path d="M5 20 L35 20 L31 36 C31 38, 28 39, 20 39 C12 39, 9 38, 9 36 Z" />
      <rect x="5" y="19" width="30" height="2" rx="1" />
    </>
  ),
  // Stack of plates — for "alt borddækning" / wedding service
  plates: (
    <>
      <ellipse cx="20" cy="20" rx="13" ry="2.6" />
      <ellipse cx="20" cy="26" rx="14.5" ry="2.8" />
      <ellipse cx="20" cy="32" rx="16" ry="3" />
    </>
  ),
  // Decorative top-down plate — Sarreguemines style with medallion
  decorPlate: (
    <>
      <circle cx="20" cy="24" r="14" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="20" cy="24" r="9" fill="none" stroke="currentColor" strokeWidth="0.8" />
      <circle cx="20" cy="24" r="3.2" />
    </>
  ),
  // Pitcher with handle
  pitcher: (
    <>
      <path d="M11 8 L25 8 L27 12 L27 36 C27 38, 25 40, 22 40 L17 40 C14 40, 12 38, 12 36 Z" />
      <path
        d="M27 14 C32 16, 32 26, 27 28"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
      />
    </>
  ),
  // Cup with handle
  cup: (
    <>
      <path d="M10 14 L26 14 L25 36 C25 38, 23 39, 18 39 C13 39, 11 38, 11 36 Z" />
      <path
        d="M26 18 C31 20, 31 30, 26 32"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
      />
    </>
  ),
  // Crossed fork + knife
  cutlery: (
    <>
      <rect x="14" y="6" width="1.4" height="6" />
      <rect x="16.5" y="6" width="1.4" height="6" />
      <rect x="19" y="6" width="1.4" height="6" />
      <path d="M13.5 12 L21 12 L20 14 L14.5 14 Z" />
      <rect x="16.5" y="14" width="1.4" height="26" />
      <path d="M27 6 L29 6 L30.5 38 L26 38 Z" />
    </>
  ),
};

const GlassShapes = {
  // Wine glass with stem and foot
  wine: (
    <>
      <path d="M11 5 C11 18, 14.5 22, 20 22 C25.5 22, 29 18, 29 5 Z" />
      <rect x="19" y="22" width="2" height="16" />
      <ellipse cx="20" cy="40" rx="7" ry="1.5" />
    </>
  ),
  // Tumbler / water glass
  tumbler: (
    <>
      <path d="M12 8 L28 8 L26 38 L14 38 Z" />
      <ellipse cx="20" cy="8" rx="8" ry="1.5" />
    </>
  ),
  // Martini / drinksglas — V-shaped cocktail glass on a stem
  lowball: (
    <>
      <path d="M6 8 L34 8 L21 24 L19 24 Z" />
      <rect x="19" y="24" width="2" height="14" />
      <ellipse cx="20" cy="40" rx="8" ry="1.6" />
    </>
  ),
  // Decanter / karaffel
  decanter: (
    <>
      <rect x="18" y="4" width="4" height="6" />
      <path d="M14 12 C14 14, 11 16, 10 22 C8 30, 10 38, 20 38 C30 38, 32 30, 30 22 C29 16, 26 14, 26 12 Z" />
    </>
  ),
  // Abstract glass-art swirl / sculpture
  glassArt: (
    <>
      <path d="M14 40 C10 32, 18 28, 16 20 C14 12, 22 6, 25 12 C28 18, 22 24, 26 32 C28 38, 26 41, 22 41 C18 41, 14 40, 14 40 Z" />
    </>
  ),
  // Shallow glass bowl
  glassBowl: (
    <>
      <path d="M5 22 L35 22 L31 32 C31 34, 28 35, 20 35 C12 35, 9 34, 9 32 Z" />
      <ellipse cx="20" cy="22" rx="15" ry="1.6" />
    </>
  ),
};

const InteriorShapes = {
  // Tall narrow vase
  vase: (
    <>
      <path d="M16 4 L24 4 L23 10 C28 14, 28 24, 26 32 C25 38, 22 42, 20 42 C18 42, 15 38, 14 32 C12 24, 12 14, 17 10 Z" />
    </>
  ),
  // Round jar / krukke
  jar: (
    <>
      <rect x="15" y="10" width="10" height="3" />
      <path d="M14 13 L26 13 C30 17, 30 32, 26 38 C24 40, 22 41, 20 41 C18 41, 16 40, 14 38 C10 32, 10 17, 14 13 Z" />
    </>
  ),
  // Candleholder with candle on top
  candle: (
    <>
      <rect x="19" y="3" width="2" height="11" />
      <path d="M14 14 L26 14 L26 18 L23 22 L17 22 L14 18 Z" />
      <rect x="18" y="22" width="4" height="14" />
      <path d="M11 36 L29 36 L31 41 L9 41 Z" />
    </>
  ),
  // Oval mirror with frame
  mirror: (
    <>
      <ellipse cx="20" cy="24" rx="11" ry="16" fill="none" stroke="currentColor" strokeWidth="2.4" />
      <ellipse cx="20" cy="24" rx="8.5" ry="13.5" />
    </>
  ),
  // Framed wall art
  frame: (
    <>
      <rect x="8" y="8" width="24" height="32" fill="none" stroke="currentColor" strokeWidth="2.2" />
      <rect x="11" y="11" width="18" height="26" />
    </>
  ),
  // Side table with two legs
  table: (
    <>
      <rect x="6" y="14" width="28" height="3" />
      <rect x="9" y="17" width="2" height="22" />
      <rect x="29" y="17" width="2" height="22" />
      <rect x="11" y="39" width="18" height="2" />
    </>
  ),
};

const MuranoShapes = {
  // Pendant / loftlampe
  pendant: (
    <>
      <rect x="19.4" y="2" width="1.2" height="10" />
      <path d="M6 24 C6 16, 12 12, 20 12 C28 12, 34 16, 34 24 C34 28, 30 30, 20 30 C10 30, 6 28, 6 24 Z" />
      <ellipse cx="20" cy="30" rx="9" ry="2.2" />
    </>
  ),
  // Multi-arm chandelier / lysekrone
  chandelier: (
    <>
      <rect x="19.4" y="2" width="1.2" height="8" />
      <circle cx="20" cy="12" r="2.6" />
      <path
        d="M20 14 C12 16, 8 22, 8 28 M20 14 C28 16, 32 22, 32 28 M20 14 L20 26"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <circle cx="8" cy="30" r="2.6" />
      <circle cx="32" cy="30" r="2.6" />
      <circle cx="20" cy="28" r="2.6" />
    </>
  ),
  // Wall sconce / væglampe — half-shape attached to a wall on the left
  sconce: (
    <>
      <rect x="2" y="6" width="2" height="36" />
      <path d="M4 22 C4 14, 10 10, 16 12 C22 14, 24 22, 22 28 C20 32, 14 32, 10 30 C6 28, 4 26, 4 22 Z" />
    </>
  ),
  // Sale tag — for "Murano på udsalg"
  tag: (
    <>
      <path d="M5 14 L24 14 L34 24 L24 34 L5 34 Z" />
      <circle cx="11" cy="20" r="2.2" fill="oklch(1 0 0)" />
    </>
  ),
};

// Mapping from collection handle → silhouette. Defaults to a generic shape
// from the parent group if not found.
const SHAPE_BY_HANDLE: Record<string, ReactNode> = {
  // Borddækning
  borddaekning: TableShapes.plates,
  bryllupsservice: TableShapes.plates,
  "sarreguemines-fajance-porcelaen": TableShapes.decorPlate,
  tallerkener: TableShapes.plate,
  fade: TableShapes.platter,
  skaale: TableShapes.bowl,
  bestik: TableShapes.cutlery,
  kander: TableShapes.pitcher,
  "keramik-krus-og-kopper": TableShapes.cup,

  // Glas
  glas: GlassShapes.wine,
  vinglas: GlassShapes.wine,
  vandglas: GlassShapes.tumbler,
  drinksglas: GlassShapes.lowball,
  glaskunst: GlassShapes.glassArt,
  glasskale: GlassShapes.glassBowl,

  // Interiør
  interior: InteriorShapes.vase,
  vaser: InteriorShapes.vase,
  krukke: InteriorShapes.jar,
  lysestager: InteriorShapes.candle,
  "vintage-spejle": InteriorShapes.mirror,
  vaegpynt: InteriorShapes.frame,
  borde: InteriorShapes.table,

  // Murano
  "murano-lamper": LAMP_SHAPES[0], // mushroom — flagship shape
  "murano-loftlamper": MuranoShapes.pendant,
  "murano-lysekrone": MuranoShapes.chandelier,
  "murano-bordlamper": LAMP_SHAPES[1], // tulip table lamp
  "murano-vaeglamper": MuranoShapes.sconce,
  "murano-mushroom-lampe": LAMP_SHAPES[0],
  "murano-vaser": InteriorShapes.vase,
  "murano-glaskunst": GlassShapes.glassArt,
  "murano-pa-udsalg": MuranoShapes.tag,
};

// Fallback shape per category, used if a sub-handle isn't in SHAPE_BY_HANDLE.
const FALLBACK_SHAPE: Record<DictKey | string, ReactNode> = {
  "nav.tableware": TableShapes.plate,
  "nav.glass": GlassShapes.wine,
  "nav.interior": InteriorShapes.vase,
  "nav.murano": LAMP_SHAPES[0],
};


export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState<DropdownKey>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { t, locale } = useT();
  const { count: wishCount } = useWishlist();

  const items = useCartStore((s) => s.items);
  const setCartOpen = useCartStore((s) => s.setOpen);
  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      } else if (e.key === "Escape") {
        setOpen(null);
        setMobileOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const openDropdown = (k: Exclude<DropdownKey, null>) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(k);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(null), 120);
  };

  function DropdownTrigger({
    k,
    label,
  }: {
    k: Exclude<DropdownKey, null>;
    label: string;
  }) {
    const isOpen = open === k;
    return (
      <div
        className="relative"
        onMouseEnter={() => openDropdown(k)}
        onMouseLeave={scheduleClose}
      >
        <button
          className="group inline-flex items-center gap-1 py-2 text-sm font-medium text-foreground/80 transition-colors hover:text-foreground"
          onClick={() => setOpen(isOpen ? null : k)}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          {label}
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
          <span className="absolute -bottom-0.5 left-0 h-px w-0 bg-terracotta transition-all duration-300 group-hover:w-[calc(100%-1rem)]" />
        </button>
      </div>
    );
  }

  // Reusable silhouette link — small SVG icon + label, used in every dropdown
  // grid (Borddækning, Glas, Interiør, Murano).
  function SilhouetteLink({
    handle,
    labelDa,
    labelEn,
    fallback,
    sizePx = 30,
  }: {
    handle: string;
    labelDa: string;
    labelEn: string;
    fallback: ReactNode;
    sizePx?: number;
  }) {
    const shape = SHAPE_BY_HANDLE[handle] ?? fallback;
    return (
      <LocaleLink
        to="/collections/$handle"
        params={{ handle }}
        onClick={() => setOpen(null)}
        className="group flex items-center gap-3 rounded-sm py-1.5 pl-1 pr-2 transition-colors hover:bg-foreground/5"
      >
        <span
          className="flex h-11 w-11 shrink-0 items-end justify-center"
          aria-hidden
        >
          <svg
            viewBox="0 0 40 48"
            width={sizePx}
            height={sizePx * 1.2}
            className="text-foreground/70 transition-all duration-300 ease-out group-hover:-translate-y-0.5 group-hover:text-terracotta"
            fill="currentColor"
          >
            {shape}
          </svg>
        </span>
        <span className="block text-sm text-foreground group-hover:text-terracotta">
          {locale === "da" ? labelDa : labelEn}
        </span>
      </LocaleLink>
    );
  }

  // Generic category mega-panel: small intro card on the left + silhouette
  // grid on the right. Used for Borddækning, Glas and Interiør.
  function CategoryPanel({ cat }: { cat: (typeof CATEGORY_NAVS)[number] }) {
    const group = findGroup(cat.groupKey);
    if (!group) return null;
    const fallback = FALLBACK_SHAPE[cat.groupKey] ?? TableShapes.plate;
    const subs = group.collections.filter((c) => c.handle !== cat.allHandle);
    return (
      <div
        className="hidden border-t border-border/60 bg-background md:block"
        onMouseEnter={() => openDropdown(cat.key)}
        onMouseLeave={scheduleClose}
      >
        <div className="mx-auto max-w-[1500px] px-10 py-10">
          <div className="grid grid-cols-12 gap-12">
            <div className="col-span-3">
              <p className="eyebrow">{cat.italian}</p>
              <h3 className="mt-3 font-display text-3xl leading-tight">
                {locale === "da" ? cat.labelDa : cat.labelEn}
              </h3>
              <p className="mt-3 max-w-[22ch] text-sm text-muted-foreground">
                {locale === "da" ? cat.blurbDa : cat.blurbEn}
              </p>
              <LocaleLink
                to="/collections/$handle"
                params={{ handle: cat.allHandle }}
                onClick={() => setOpen(null)}
                className="mt-5 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-terracotta hover:text-foreground"
              >
                {locale === "da" ? "Se hele udvalget" : "See the full range"} →
              </LocaleLink>
            </div>
            <div className="col-span-9 grid grid-cols-2 gap-x-6 gap-y-2 lg:grid-cols-3">
              {subs.map((c) => (
                <SilhouetteLink
                  key={c.handle}
                  handle={c.handle}
                  labelDa={c.labelDa}
                  labelEn={c.labelEn}
                  fallback={fallback}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled || open
            ? "border-b border-border/60 bg-background/95 backdrop-blur-md"
            : "bg-background/40 backdrop-blur-sm"
        }`}
      >
        {/* Slim utility strip — secondary nav: showroom, about, blog, social, book */}
        <div className="hidden border-b border-border/40 py-2 text-[10px] uppercase tracking-[0.22em] md:block">
          <div className="mx-auto flex max-w-[1500px] items-center justify-between px-6 md:px-10">
            <span className="text-foreground/75">
              {t("topbar.dispatch")} · {t("home.hero.strip2")}
            </span>

            <div className="flex items-center gap-6">
              <LocaleLink
                to="/showroom"
                className="group relative inline-flex items-center text-foreground/75 transition-colors hover:text-foreground"
              >
                {t("header.showroom")}
                <span className="absolute -bottom-0.5 left-0 h-px w-0 bg-foreground transition-all duration-300 group-hover:w-full" />
              </LocaleLink>
              <LocaleLink
                to="/about"
                className="group relative inline-flex items-center text-foreground/75 transition-colors hover:text-foreground"
              >
                {t("header.about")}
                <span className="absolute -bottom-0.5 left-0 h-px w-0 bg-foreground transition-all duration-300 group-hover:w-full" />
              </LocaleLink>
              <LocaleLink
                to="/blog"
                className="group relative inline-flex items-center text-foreground/75 transition-colors hover:text-foreground"
              >
                {t("header.blog")}
                <span className="absolute -bottom-0.5 left-0 h-px w-0 bg-foreground transition-all duration-300 group-hover:w-full" />
              </LocaleLink>
              <a
                href="https://www.instagram.com/farveladeland/"
                target="_blank"
                rel="noreferrer"
                className="group relative inline-flex items-center text-foreground/75 transition-colors hover:text-foreground"
              >
                @farveladeland
                <span className="absolute -bottom-0.5 left-0 h-px w-0 bg-foreground transition-all duration-300 group-hover:w-full" />
              </a>
              <LocaleLink
                to="/visit"
                className="inline-flex items-center gap-1.5 rounded-full border border-terracotta/60 px-2.5 py-1 text-terracotta transition-colors hover:border-terracotta hover:bg-terracotta hover:text-primary-foreground"
              >
                {t("topbar.book")} <span aria-hidden>→</span>
              </LocaleLink>
              <span data-tour="lang"><LangSwitcher /></span>
            </div>
          </div>
        </div>

        <div className="mx-auto flex max-w-[1500px] flex-col items-start justify-start gap-2 px-6 py-2.5 md:flex-row md:items-center md:justify-between md:gap-0 md:px-10 md:py-4">
          <LocaleLink to="/" className="flex items-baseline gap-2" aria-label="Farveladeland">
            <span className="font-display text-xl tracking-tight text-foreground md:text-2xl">
              Farveladeland
            </span>
          </LocaleLink>

          {/* Slim nav: Murano · Farver · Størrelse · Borddækning · Glas · Interiør */}
          <nav className="hidden flex-nowrap items-center gap-4 whitespace-nowrap md:flex lg:gap-6" aria-label="Main">
            {/* Temporary launch item — "Fund fra Japan" collection */}
            <LocaleLink
              to="/collections/$handle"
              params={{ handle: "fund-fra-japan" }}
              className="group relative inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium text-foreground transition-colors hover:text-terracotta"
            >
              {t("header.japanCollection")}
              <span className="inline-flex h-4 min-w-[1.25rem] items-center justify-center rounded-full bg-terracotta px-1.5 text-[9px] font-semibold uppercase tracking-wider text-primary-foreground">
                {t("header.newBadge")}
              </span>
            </LocaleLink>

            <DropdownTrigger k="murano" label={t("header.murano")} />
            <DropdownTrigger k="colors" label={t("header.colors")} />
            <DropdownTrigger k="sizes" label={t("header.sizes")} />
            {CATEGORY_NAVS.map((cat) => (
              <DropdownTrigger
                key={cat.key}
                k={cat.key}
                label={locale === "da" ? cat.labelDa : cat.labelEn}
              />
            ))}
            <span aria-hidden className="h-4 w-px bg-border" />
            <LocaleLink
              to="/alle"
              className="group relative inline-flex items-center whitespace-nowrap text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {t("header.allProducts")}
              <span className="absolute -bottom-0.5 left-0 h-px w-0 bg-foreground transition-all duration-300 group-hover:w-full" />
            </LocaleLink>
          </nav>

          <div className="flex w-full items-center justify-between gap-4 md:w-auto md:justify-start md:self-auto md:gap-2">
            <button
              data-tour="search"
              onClick={() => setSearchOpen(true)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-foreground/80 transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta"
              aria-label={t("header.search")}
              title={`${t("header.search")} · ⌘K`}
            >
              <Search className="h-4 w-4" />
            </button>
            <LocaleLink
              data-tour="wishlist"
              to="/onskeliste"
              className="relative inline-flex h-9 w-9 items-center justify-center rounded-full text-foreground/80 transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta"
              aria-label={`${t("wishlist.nav")} (${wishCount})`}
              title={t("wishlist.nav")}
            >
              <Heart
                className="h-4 w-4"
                fill={wishCount > 0 ? "currentColor" : "none"}
                strokeWidth={1.6}
              />
              {wishCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-terracotta px-1 text-[9px] font-medium text-primary-foreground">
                  {wishCount}
                </span>
              )}
            </LocaleLink>
            <div data-tour="lang" className="md:hidden">
              <LangSwitcher />
            </div>
            <button
              onClick={() => setCartOpen(true)}
              className="group relative inline-flex items-center gap-2 rounded-full border border-foreground/20 px-3 py-2 text-xs font-medium uppercase tracking-[0.18em] text-foreground transition-colors hover:border-foreground hover:bg-foreground hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              aria-label={`${t("header.basket")}, ${totalItems}`}
            >
              <ShoppingBag className="h-4 w-4" />
              <span className="hidden md:inline">{t("header.basket")}</span>
              {totalItems > 0 && (
                <span className="ml-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-terracotta px-1 text-[10px] font-medium text-primary-foreground">
                  {totalItems}
                </span>
              )}
            </button>
            <button
              className="md:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={mobileOpen ? t("header.menuClose") : t("header.menuOpen")}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* === Murano dropdown === */}
        {open === "murano" && MURANO_GROUP && (
          <div
            className="hidden border-t border-border/60 bg-background md:block"
            onMouseEnter={() => openDropdown("murano")}
            onMouseLeave={scheduleClose}
          >
            <div className="mx-auto max-w-[1500px] px-10 py-10">
              <div className="grid grid-cols-12 gap-12">
                <div className="col-span-3">
                  <p className="eyebrow">{MURANO_GROUP.italian}</p>
                  <h3 className="mt-3 font-display text-3xl leading-tight">
                    {t("header.murano")}
                  </h3>
                  <LocaleLink
                    to="/murano"
                    onClick={() => setOpen(null)}
                    className="mt-5 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-terracotta hover:text-foreground"
                  >
                    {t("murano.shopCta")} →
                  </LocaleLink>
                  <LocaleLink
                    to="/murano/match"
                    onClick={() => setOpen(null)}
                    className="mt-3 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-foreground/70 hover:text-terracotta"
                  >
                    ✦ {t("match.cta")} →
                  </LocaleLink>
                </div>
                <div className="col-span-9 grid grid-cols-2 gap-x-6 gap-y-2 lg:grid-cols-3">
                  {MURANO_GROUP.collections.map((c) => (
                    <SilhouetteLink
                      key={c.handle}
                      handle={c.handle}
                      labelDa={c.labelDa}
                      labelEn={c.labelEn}
                      fallback={LAMP_SHAPES[0]}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* === Category dropdowns: Belysning, Borddækning, Glas, Interiør === */}
        {CATEGORY_NAVS.map((cat) =>
          open === cat.key ? <CategoryPanel key={cat.key} cat={cat} /> : null,
        )}

        {/* === Farver dropdown === */}
        {open === "colors" && (
          <div
            className="hidden border-t border-border/60 bg-background md:block"
            onMouseEnter={() => openDropdown("colors")}
            onMouseLeave={scheduleClose}
          >
            <div className="mx-auto max-w-[1500px] px-10 py-10">
              <div className="grid grid-cols-12 gap-12">
                <div className="col-span-3">
                  <p className="eyebrow">{t("header.shopByColor")}</p>
                  <h3 className="mt-3 font-display text-3xl leading-tight">
                    {t("header.colors")}
                  </h3>
                  <LocaleLink
                    to="/farver"
                    onClick={() => setOpen(null)}
                    className="mt-5 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-terracotta hover:text-foreground"
                  >
                    {t("header.allColors")} →
                  </LocaleLink>
                </div>
                <div className="col-span-9 grid grid-cols-4 gap-x-6 gap-y-4">
                  {COLOR_CATEGORIES.map((c, i) => {
                    const shape = LAMP_SHAPES[i % LAMP_SHAPES.length];
                    return (
                      <LocaleLink
                        key={c.slug}
                        to="/farver/$slug"
                        params={{ slug: c.slug }}
                        onClick={() => setOpen(null)}
                        className="group flex items-center gap-3 rounded-sm py-1.5 pl-1 pr-2 transition-colors hover:bg-foreground/5"
                      >
                        <span
                          className="flex h-9 w-7 shrink-0 items-end justify-center"
                          aria-hidden
                        >
                          <svg
                            viewBox="0 0 40 48"
                            width={26}
                            height={32}
                            fill={c.swatch}
                            className="drop-shadow-[0_1px_0_rgba(0,0,0,0.04)] transition-transform duration-300 ease-out group-hover:-translate-y-0.5"
                          >
                            <g stroke="oklch(0.24 0.025 285 / 0.35)" strokeWidth="0.6">
                              {shape}
                            </g>
                          </svg>
                        </span>
                        <span>
                          <span className="block text-sm text-foreground group-hover:text-terracotta">
                            {locale === "da" ? c.labelDa : c.labelEn}
                          </span>
                          <span className="block text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                            {c.italian}
                          </span>
                        </span>
                      </LocaleLink>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* === Størrelse dropdown === */}
        {open === "sizes" && (
          <div
            className="hidden border-t border-border/60 bg-background md:block"
            onMouseEnter={() => openDropdown("sizes")}
            onMouseLeave={scheduleClose}
          >
            <div className="mx-auto max-w-[1500px] px-10 py-10">
              <div className="grid grid-cols-12 gap-12">
                <div className="col-span-3">
                  <p className="eyebrow">{t("header.shopBySize")}</p>
                  <h3 className="mt-3 font-display text-3xl leading-tight">
                    {t("header.sizes")}
                  </h3>
                  <LocaleLink
                    to="/storrelse"
                    onClick={() => setOpen(null)}
                    className="mt-5 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-terracotta hover:text-foreground"
                  >
                    {t("header.allSizes")} →
                  </LocaleLink>
                </div>
                <div className="col-span-9 grid grid-cols-2 gap-x-6 gap-y-4 lg:grid-cols-4">
                  {SIZE_BUCKETS.map((b) => {
                    const mid = b.max === Infinity ? 55 : (b.min + b.max) / 2;
                    const sizePx = Math.round(Math.min(36, 14 + mid * 0.42));
                    return (
                      <LocaleLink
                        key={b.slug}
                        to="/storrelse/$range"
                        params={{ range: b.slug }}
                        onClick={() => setOpen(null)}
                        className="group flex items-center gap-3 rounded-sm py-1.5 pl-1 pr-2 transition-colors hover:bg-foreground/5"
                      >
                        <span
                          className="flex h-10 w-10 shrink-0 items-end justify-center"
                          aria-hidden
                        >
                          <svg
                            viewBox="0 0 40 48"
                            width={sizePx}
                            height={sizePx * 1.2}
                            className="text-foreground/70 transition-colors group-hover:text-terracotta"
                            fill="currentColor"
                          >
                            <path d="M4 22 C4 10, 14 3, 20 3 C26 3, 36 10, 36 22 Z" />
                            <rect x="14" y="22" width="12" height="14" rx="1" />
                            <rect x="10" y="36" width="20" height="3" rx="1.5" />
                          </svg>
                        </span>
                        <span>
                          <span className="block text-sm text-foreground group-hover:text-terracotta">
                            {locale === "da" ? b.labelDa : b.labelEn}
                          </span>
                          <span className="block text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                            {b.italian}
                          </span>
                        </span>
                      </LocaleLink>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {mobileOpen && (
          <div className="max-h-[calc(100svh-64px)] overflow-y-auto border-t border-border bg-background md:hidden">
            <div className="px-6 py-4">
              <button
                onClick={() => {
                  setMobileOpen(false);
                  setSearchOpen(true);
                }}
                className="mb-3 flex w-full items-center gap-3 rounded-sm border border-border px-4 py-3 text-left text-sm text-muted-foreground hover:border-foreground/40"
              >
                <Search className="h-4 w-4" />
                {t("header.searchPlaceholder")}
              </button>

              <LocaleLink
                to="/collections/$handle"
                params={{ handle: "fund-fra-japan" }}
                onClick={() => setMobileOpen(false)}
                className="mb-1 flex items-center justify-between border-b border-border py-3 font-display text-lg text-foreground/90 hover:text-terracotta"
              >
                {t("header.japanCollection")}
                <span className="inline-flex h-5 min-w-[1.5rem] items-center justify-center rounded-full bg-terracotta px-2 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground">
                  {t("header.newBadge")}
                </span>
              </LocaleLink>

              <LocaleLink
                to="/alle"
                onClick={() => setMobileOpen(false)}
                className="mb-1 block border-b border-border py-3 font-display text-lg text-foreground/90 hover:text-terracotta"
              >
                {t("header.allProducts")}
              </LocaleLink>


              {/* One accordion per top-level category, mirroring the desktop nav */}
              {navGroups
                .filter((g) => g.labelKey !== "nav.sale")
                .map((g, idx) => (
                  <details
                    key={g.labelKey}
                    className="border-b border-border py-3"
                    open={idx === 0}
                  >
                    <summary className="flex cursor-pointer items-center justify-between font-display text-lg">
                      {t(g.labelKey)}
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    </summary>
                    <ul className="mt-3 space-y-1 pl-1">
                      {g.collections.map((c) => (
                        <li key={c.handle}>
                          <LocaleLink
                            to="/collections/$handle"
                            params={{ handle: c.handle }}
                            onClick={() => setMobileOpen(false)}
                            className="block py-1 text-sm text-foreground/80 hover:text-terracotta"
                          >
                            {locale === "da" ? c.labelDa : c.labelEn}
                          </LocaleLink>
                        </li>
                      ))}
                    </ul>
                  </details>
                ))}

              <details className="border-b border-border py-3">
                <summary className="flex cursor-pointer items-center justify-between font-display text-lg">
                  {t("header.colors")}
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </summary>
                <ul className="mt-3 grid grid-cols-2 gap-y-1 pl-1">
                  {COLOR_CATEGORIES.map((c) => (
                    <li key={c.slug}>
                      <LocaleLink
                        to="/farver/$slug"
                        params={{ slug: c.slug }}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-2 py-1.5 text-sm text-foreground/80 hover:text-terracotta"
                      >
                        <span
                          className="block h-3.5 w-3.5 rounded-full border border-foreground/15"
                          style={{ background: c.swatch }}
                          aria-hidden
                        />
                        {locale === "da" ? c.labelDa : c.labelEn}
                      </LocaleLink>
                    </li>
                  ))}
                </ul>
                <LocaleLink
                  to="/farver"
                  onClick={() => setMobileOpen(false)}
                  className="mt-2 inline-block text-xs font-medium uppercase tracking-[0.22em] text-terracotta"
                >
                  {t("header.allColors")} →
                </LocaleLink>
              </details>

              <details className="border-b border-border py-3">
                <summary className="flex cursor-pointer items-center justify-between font-display text-lg">
                  {t("header.sizes")}
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </summary>
                <ul className="mt-3 space-y-1 pl-1">
                  {SIZE_BUCKETS.map((b) => (
                    <li key={b.slug}>
                      <LocaleLink
                        to="/storrelse/$range"
                        params={{ range: b.slug }}
                        onClick={() => setMobileOpen(false)}
                        className="block py-1 text-sm text-foreground/80 hover:text-terracotta"
                      >
                        {locale === "da" ? b.labelDa : b.labelEn}
                      </LocaleLink>
                    </li>
                  ))}
                </ul>
              </details>

              <LocaleLink
                to="/murano/match"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 border-b border-border py-4 font-display text-lg"
              >
                <span aria-hidden className="text-terracotta">✦</span>
                {t("header.match")}
              </LocaleLink>
              <LocaleLink
                to="/showroom"
                onClick={() => setMobileOpen(false)}
                className="block border-b border-border py-4 font-display text-lg"
              >
                {t("header.showroom")}
              </LocaleLink>
              <LocaleLink
                to="/blog"
                onClick={() => setMobileOpen(false)}
                className="block border-b border-border py-4 font-display text-lg"
              >
                {t("header.blog")}
              </LocaleLink>
              <LocaleLink
                to="/about"
                onClick={() => setMobileOpen(false)}
                className="block border-b border-border py-4 font-display text-lg"
              >
                {t("header.about")}
              </LocaleLink>
              <LocaleLink
                to="/visit"
                onClick={() => setMobileOpen(false)}
                className="block py-4 font-display text-lg text-terracotta"
              >
                {t("topbar.book")} →
              </LocaleLink>
            </div>
          </div>
        )}
      </header>

      <SearchPanel open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}
